// Strategy Types
export const StrategyStatus = {
  DRAFT: 'DRAFT',
  TESTING: 'TESTING',
  LIVE: 'LIVE',
  ARCHIVED: 'ARCHIVED',
} as const;

export const StrategyMode = {
  EXPERIMENTAL: 'EXPERIMENTAL',
  PAPER_TRADING: 'PAPER_TRADING',
  LIVE_TRADING: 'LIVE_TRADING',
  BACKTEST: 'BACKTEST',
} as const;

export const StrategyType = {
  MOMENTUM: 'MOMENTUM',
  MEAN_REVERSION: 'MEAN_REVERSION',
  TREND_FOLLOWING: 'TREND_FOLLOWING',
  ARBITRAGE: 'ARBITRAGE',
  MARKET_MAKING: 'MARKET_MAKING',
  CUSTOM: 'CUSTOM',
  BOOLEAN_LOGIC: 'BOOLEAN_LOGIC',
} as const;

// Marketplace status for strategies
export const MarketplaceStatus = {
  PRIVATE: 'PRIVATE',
  PUBLISHED: 'PUBLISHED',
  FEATURED: 'FEATURED',
} as const;

export type StrategyStatus = typeof StrategyStatus[keyof typeof StrategyStatus];
export type StrategyMode = typeof StrategyMode[keyof typeof StrategyMode];
export type StrategyType = typeof StrategyType[keyof typeof StrategyType];
export type MarketplaceStatus = typeof MarketplaceStatus[keyof typeof MarketplaceStatus];

export interface StrategyParameter {
  name: string;
  type: 'number' | 'string' | 'boolean';
  value: any;
  label: string;
  required: boolean;
}

export interface TechnicalIndicator {
  id: string;
  name: string;
  type: 'RSI' | 'MACD' | 'MA' | 'BOLLINGER' | 'STOCHASTIC';
  parameters: StrategyParameter[];
}

// Condition types for control flow nodes
export interface Condition {
  id: string;
  type: 'price' | 'indicator' | 'volume';
  indicator?: string;
  symbol?: string;
  exchange?: string;
  operator: 'greater_than' | 'less_than' | 'equals' | 'crosses_above' | 'crosses_below';
  value: number;
}

export interface ConditionGroup {
  id: string;
  operator: 'and' | 'or';
  conditions: Condition[];
}

export interface ControlFlow {
  id: string;
  type: 'controlFlow';
  flowType: 'if-then' | 'if-then-else';
  conditions: ConditionGroup[];
}

export interface SignalRule {
  id: string;
  indicatorId: string;
  condition: 'CROSSES_ABOVE' | 'CROSSES_BELOW' | 'GREATER_THAN' | 'LESS_THAN' | 'EQUALS';
  value: number | string;
  action: 'BUY' | 'SELL';
}

export interface BasicStrategyConfig {
  indicators: TechnicalIndicator[];
  buyRules: SignalRule[];
  sellRules: SignalRule[];
  riskManagement: {
    stopLoss: number;
    takeProfit: number;
    maxPositionSize: number;
  }
}

export interface AdvancedStrategyConfig {
  code: string;
  language: 'python' | 'javascript';
  dependencies: string[];
}

export interface BacktestResult {
  startDate: string;
  endDate: string;
  totalReturns: number;
  sharpeRatio: number;
  maxDrawdown: number;
  winRate: number;
  trades: number;
}

// Visual Strategy Builder Types
export interface Indicator {
  id: string;
  type: 'indicator';
  name: string;
  component: 'sma' | 'ema' | 'rsi' | 'macd' | 'bollinger' | 'volume' | 'price';
  parameters: {
    [key: string]: number | string;
  };
}

export interface LogicOperator {
  id: string;
  type: 'operator';
  operation: 'and' | 'or' | 'not';
}

export interface ComparisonCondition {
  id: string;
  type: 'condition';
  indicator1: string;
  indicator2?: string;
  comparison: 'crosses_above' | 'crosses_below' | 'greater_than' | 'less_than' | 'equals' | 'equal_to';
  value?: number;
}

export interface Signal {
  id: string;
  type: 'signal';
  action: 'buy' | 'sell';
  size: number;
  conditions: string[];
}

export interface ControlFlowNode {
  id: string;
  type: 'controlFlow';
  name: string;
  flowType: 'if-then' | 'if-then-else';
  conditions: ConditionGroup[];
}

export interface VisualStrategy {
  indicators: Indicator[];
  operators: LogicOperator[];
  conditions: ComparisonCondition[];
  signals: Signal[];
  controlFlows?: ControlFlowNode[];
}

export interface Strategy {
  id?: string;
  userId?: string;
  authorName?: string;
  name: string;
  description: string;
  mode: StrategyMode;
  type: StrategyType;
  status: StrategyStatus;
  code: string;
  parameters: {
    timeframe: string;
    universe: string[];
    exchange: string;
    risk_management: {
      max_position_size: number;
      stop_loss: number;
    };
  };
  tags?: string[];
  marketplaceStatus: MarketplaceStatus;
  backtestResults?: BacktestResult;
  visualStrategy?: VisualStrategy;
  flow?: {
    nodes: any[];
    edges: any[];
  };
  metadata?: {
    conditions?: any[];
    actions?: any[];
    [key: string]: any;
  };
  createdAt?: string;
  updatedAt?: string;
}
